import { useState } from "react"
import type { CSSProperties, FC, ReactNode } from "react"
import { Tooltip } from "antd"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import { compactTooltipStyles, getShadowTooltipContainer } from "../shadowTooltip"
import styles from "./SliderPopover.module.scss"

type Props = {
	value: number
	onChange: (value: number) => void
	min: number
	max: number
	/** Appended to the trigger's displayed value, e.g. "%" or " pt". */
	unit?: string
	icon: ReactNode
	/** Accessible name + tooltip text — distinguishes multiple sliders in the same toolbar. */
	label: string
	/** Shows a numeric input beside the slider (the Figma "Stroke / Opened" mock; Opacity has none). */
	showNumberInput?: boolean
}

const POPOVER_WIDTH = 200

const popoverStyle: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 10,
	width: POPOVER_WIDTH,
	padding: "10px 12px",
	background: "#fff",
	border: "1px solid var(--clr-lightGray, #dee0eb)",
	borderRadius: 8,
	boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
}

const inputStyle: CSSProperties = {
	width: 40,
	height: 24,
	padding: "0 4px",
	border: "1px solid var(--clr-lightGray, #dee0eb)",
	borderRadius: 4,
	textAlign: "center",
	fontSize: 12,
}

// Unique per-mount so two sliders open at once (unlikely, but the two color pickers already prove it
// happens) never fight over the same global `<style>` selector.
let instanceCounter = 0

/**
 * Small reusable slider dropdown (REQ-400 §1381 review): an icon + current value + chevron trigger,
 * opening a native range input in a floating popover directly below it. Used for Opacity (0-100%)
 * and Stroke width (1-20pt, with an inline numeric input alongside — Figma "Stroke / Opened").
 *
 * Renders via `popupRender` with fully inline styles, exactly like `ColorSwatchPicker` (see that
 * component's own comment for why `renderInPlace` is the wrong tool here). The one piece that CAN'T
 * be inline-styled is the range input's thumb (`::-webkit-slider-thumb` / `::-moz-range-thumb` are
 * pseudo-elements — a JS `style` prop cannot reach them, and a CSS Module class risks not reaching
 * the popup either, if it portals outside this stylesheet's shadow root). A `<style>` tag written
 * directly into the JSX travels WITH the subtree it's rendered into regardless of where that
 * subtree ends up in the DOM, so it works whether the popup lands in the shadow root or is portaled
 * to `document.body` — no external stylesheet dependency either way.
 */
export const SliderPopover: FC<Props> = ({ value, onChange, min, max, unit = "", icon, label, showNumberInput }) => {
	const [open, setOpen] = useState(false)
	const [thumbClass] = useState(() => `wv-slider-popover-thumb-${++instanceCounter}`)

	const clamp = (next: number) => Math.min(max, Math.max(min, next))

	return (
		<Dropdown
			items={[]}
			placement="bottomLeft"
			open={open}
			onOpenChange={setOpen}
			trigger={["click"]}
			overlayStyle={{ zIndex: 1500 }}
			popupRender={() => (
				<div style={popoverStyle}>
					{/* Inline, scoped, non-user-controlled CSS text — see the component comment on why this
					    can't be a CSS Module class. This repo's eslint config has no react/no-danger rule
					    (no eslint-plugin-react), so no disable directive is needed here. */}
					<style
						dangerouslySetInnerHTML={{
							__html: `
								.${thumbClass} { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; border-radius: 2px; background: var(--clr-lightGray, #dee0eb); outline: none; }
								.${thumbClass}::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 14px; height: 14px; border-radius: 50%; background: var(--clr-main, #0047ff); cursor: pointer; }
								.${thumbClass}::-moz-range-thumb { width: 14px; height: 14px; border: none; border-radius: 50%; background: var(--clr-main, #0047ff); cursor: pointer; }
							`,
						}}
					/>
					<input
						type="range"
						className={thumbClass}
						min={min}
						max={max}
						value={value}
						aria-label={label}
						onChange={(event) => onChange(clamp(Number(event.target.value)))}
					/>
					{showNumberInput ? (
						<input
							type="number"
							style={inputStyle}
							min={min}
							max={max}
							value={value}
							aria-label={`${label} value`}
							onChange={(event) => {
								// Rounded, not just clamped: every field this drives (opacity, stroke width) is
								// declared `type: integer` by the API, so typing "2.5" would otherwise be sent
								// verbatim and rejected with a 400.
								const next = Number(event.target.value)
								if (!Number.isNaN(next)) onChange(Math.round(clamp(next)))
							}}
						/>
					) : null}
				</div>
			)}
		>
			<Tooltip title={label} placement="bottom" getPopupContainer={getShadowTooltipContainer} styles={compactTooltipStyles}>
				<button type="button" className={styles.trigger} aria-label={label}>
					<span className={styles.icon}>{icon}</span>
					<span className={styles.value}>
						{value}
						{unit}
					</span>
				</button>
			</Tooltip>
		</Dropdown>
	)
}
